const PERCENT_FORMATTER = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

export function resolveFleetSegmentComparisonLabel() {
  return "Temps HERE sans trafic";
}

export function resolveFleetSegmentComparisonBadgeLabel() {
  return "Sans trafic";
}

export function resolveFleetSegmentCurrentRoutingLabel(hereTimeAwareRoutingEnabled) {
  return hereTimeAwareRoutingEnabled === true ? "Trafic HERE" : "Sans trafic";
}

export function formatFleetSegmentComparisonDurationLabel(minutes) {
  const numericValue = Number(minutes);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return "—";
  }

  const roundedMinutes = Math.round(numericValue);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;
  if (hours <= 0) {
    return `${remainingMinutes} min`;
  }
  if (remainingMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${remainingMinutes} min`;
}

export function parseFleetSegmentDisplayedDurationMinutes(label) {
  const normalizedLabel = typeof label === "string"
    ? label.replace(/^≈\s*/u, "").trim()
    : "";

  if (!normalizedLabel || normalizedLabel === "—") {
    return null;
  }

  const hoursMatch = normalizedLabel.match(/(\d+)\s*h\b/i);
  const minutesMatch = normalizedLabel.match(/(\d+)\s*min\b/i)
    || normalizedLabel.match(/\d+\s*h\s+(\d+)\b/i);

  if (!hoursMatch && !minutesMatch) {
    return null;
  }

  const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
  const totalMinutes = (hours * 60) + minutes;

  return Number.isFinite(totalMinutes) ? totalMinutes : null;
}

export function resolveFleetSegmentDurationMinutesFromChips(chips = [], targetKind = "") {
  const normalizedTargetKind = typeof targetKind === "string" ? targetKind.trim() : "";

  if (!normalizedTargetKind) {
    return null;
  }

  const resolvedChips = Array.isArray(chips) ? chips.filter(Boolean) : [];
  let matched = false;
  let totalMinutes = 0;

  resolvedChips.forEach((chip) => {
    const chipKind = typeof chip?.kind === "string"
      ? chip.kind.trim()
      : typeof chip?.key === "string"
        ? chip.key.trim()
        : "";

    if (chipKind !== normalizedTargetKind) {
      return;
    }

    const chipMinutes = parseFleetSegmentDisplayedDurationMinutes(chip?.label);
    if (chipMinutes == null) {
      return;
    }

    matched = true;
    totalMinutes += chipMinutes;
  });

  return matched ? totalMinutes : null;
}

function normalizeFleetSegmentComparisonDurationMinutes(value) {
  if (value == null || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
}

export function resolveFleetSegmentCurrentHereDurationMinutes({
  overrideMinutes = null,
  payloadAvailable = false,
  payloadMinutes = null,
  fallbackMinutes = null,
} = {}) {
  const normalizedOverrideMinutes = normalizeFleetSegmentComparisonDurationMinutes(overrideMinutes);
  if (normalizedOverrideMinutes != null) {
    return normalizedOverrideMinutes;
  }

  const normalizedPayloadMinutes = normalizeFleetSegmentComparisonDurationMinutes(payloadMinutes);
  if (payloadAvailable && normalizedPayloadMinutes != null) {
    return normalizedPayloadMinutes;
  }

  return normalizeFleetSegmentComparisonDurationMinutes(fallbackMinutes);
}

export function resolveFleetGroupedServiceDurationMinutes({
  memberSegments = [],
  serviceRateTonsPerHour = null,
  fixedMinutes = 0,
} = {}) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const normalizedServiceRateTonsPerHour = Number(serviceRateTonsPerHour);
  const totalQuantityTons = resolvedMemberSegments.reduce((total, segment) => {
    const quantityTons = Number(segment?.quantityTons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);

  let baseServiceMinutes = 0;
  let hasFormulaResult = false;

  if (
    Number.isFinite(totalQuantityTons)
    && totalQuantityTons > 0
    && Number.isFinite(normalizedServiceRateTonsPerHour)
    && normalizedServiceRateTonsPerHour > 0
  ) {
    baseServiceMinutes = Math.ceil((((totalQuantityTons / normalizedServiceRateTonsPerHour) * 60) - 1e-9));
    hasFormulaResult = true;
  }

  let meunerieExtra = 0;
  const seenClients = new Set();
  resolvedMemberSegments.forEach((segment) => {
    if (segment?.is_preloaded_previous_day) {
      return;
    }
    const extraMinutes = Number(segment?.meunerie_loading_extra_minutes);
    if (Number.isFinite(extraMinutes) && extraMinutes > 0) {
      const clientKey = (segment?.loading_client_name || segment?.client_name || segment?.clientName || segment?.order_id || "").trim();
      if (clientKey && !seenClients.has(clientKey)) {
        seenClients.add(clientKey);
        meunerieExtra += extraMinutes;
      }
    }
  });

  // Temps fixe par livraison ou par chargement : une fois pour la visite, si elle compte une opération ordinaire.
  const visitFixedMinutes = resolvedMemberSegments.some((segment) => !segment?.requiresAspirator
    && !segment?.isPreloadedPreviousDay && !segment?.is_preloaded_previous_day && Number(segment?.quantityTons) > 0)
    ? Math.max(0, Math.round(Number(fixedMinutes) || 0))
    : 0;

  if (hasFormulaResult) {
    return baseServiceMinutes + meunerieExtra + visitFixedMinutes;
  }

  let hasResolvedMinutes = false;
  let totalServiceDurationMinutes = 0;

  resolvedMemberSegments.forEach((segment) => {
    const normalizedServiceDurationMinutes = segment?.serviceDurationMinutes == null
      ? null
      : normalizeFleetSegmentComparisonDurationMinutes(segment.serviceDurationMinutes);
    if (normalizedServiceDurationMinutes != null) {
      hasResolvedMinutes = true;
      totalServiceDurationMinutes += normalizedServiceDurationMinutes;
      return;
    }

    const normalizedVisualServiceDurationMinutes = segment?.visualServiceDurationMinutes == null
      ? null
      : normalizeFleetSegmentComparisonDurationMinutes(segment.visualServiceDurationMinutes);
    if (normalizedVisualServiceDurationMinutes != null) {
      hasResolvedMinutes = true;
      totalServiceDurationMinutes += normalizedVisualServiceDurationMinutes;
    }
  });

  return hasResolvedMinutes ? totalServiceDurationMinutes : 0;
}

export function resolveFleetSegmentDisplayedTotalDurationMinutes({
  currentDurationMinutes,
  currentHereTrafficDurationAvailable = false,
  currentHereTrafficDurationMinutes = null,
  manualBufferMinutes = 0,
  displayedServiceDurationMinutes = 0,
  displayedTravelDurationMinutes = null,
  hereTimeAwareRoutingEnabled = false,
} = {}) {
  const normalizedServiceDurationMinutes = Number(displayedServiceDurationMinutes);
  const safeServiceDurationMinutes = Number.isFinite(normalizedServiceDurationMinutes) && normalizedServiceDurationMinutes >= 0
    ? normalizedServiceDurationMinutes
    : 0;
  const normalizedManualBufferMinutes = Number(manualBufferMinutes);
  const safeManualBufferMinutes = Number.isFinite(normalizedManualBufferMinutes) && normalizedManualBufferMinutes >= 0
    ? normalizedManualBufferMinutes
    : 0;
  const normalizedCurrentHereTrafficDurationMinutes = Number(currentHereTrafficDurationMinutes);
  const normalizedDisplayedTravelDurationMinutes = Number(displayedTravelDurationMinutes);
  const normalizedCurrentDurationMinutes = Number(currentDurationMinutes);

  const referenceTravelDurationMinutes = hereTimeAwareRoutingEnabled === true
    && currentHereTrafficDurationAvailable === true
    && Number.isFinite(normalizedCurrentHereTrafficDurationMinutes)
    && normalizedCurrentHereTrafficDurationMinutes >= 0
    ? normalizedCurrentHereTrafficDurationMinutes
    : Number.isFinite(normalizedDisplayedTravelDurationMinutes) && normalizedDisplayedTravelDurationMinutes >= 0
      ? normalizedDisplayedTravelDurationMinutes
      : Number.isFinite(normalizedCurrentDurationMinutes) && normalizedCurrentDurationMinutes >= 0
        ? normalizedCurrentDurationMinutes
        : 0;

  return referenceTravelDurationMinutes + safeServiceDurationMinutes + safeManualBufferMinutes;
}

export function resolveFleetSegmentDisplayedEndMinutes(startMinutes, displayedTotalDurationMinutes) {
  if (startMinutes == null || displayedTotalDurationMinutes == null) {
    return null;
  }

  const normalizedStartMinutes = Number(startMinutes);
  const normalizedDisplayedTotalDurationMinutes = Number(displayedTotalDurationMinutes);

  if (!Number.isFinite(normalizedStartMinutes)) {
    return null;
  }

  if (!Number.isFinite(normalizedDisplayedTotalDurationMinutes) || normalizedDisplayedTotalDurationMinutes < 0) {
    return null;
  }

  return normalizedStartMinutes + normalizedDisplayedTotalDurationMinutes;
}

export function buildFleetSegmentComparisonDelta(currentDurationMinutes, comparisonDurationMinutes) {
  const currentValue = Number(currentDurationMinutes);
  const comparisonValue = Number(comparisonDurationMinutes);
  if (!Number.isFinite(currentValue) || !Number.isFinite(comparisonValue) || comparisonValue < 0) {
    return {
      deltaMinutes: null,
      deltaLabel: "",
      deltaPercent: null,
      deltaPercentLabel: "",
    };
  }

  const deltaMinutes = Math.round((currentValue - comparisonValue) * 10) / 10;
  const absoluteDeltaLabel = formatFleetSegmentComparisonDurationLabel(Math.abs(deltaMinutes));
  const deltaLabel = deltaMinutes > 0
    ? `+${absoluteDeltaLabel}`
    : deltaMinutes < 0
      ? `-${absoluteDeltaLabel}`
      : absoluteDeltaLabel;

  if (comparisonValue <= 0) {
    return {
      deltaMinutes,
      deltaLabel,
      deltaPercent: null,
      deltaPercentLabel: "",
    };
  }

  const deltaPercent = Math.round(((deltaMinutes / comparisonValue) * 100) * 10) / 10;
  const absolutePercentLabel = `${PERCENT_FORMATTER.format(Math.abs(deltaPercent))} %`;
  const deltaPercentLabel = deltaPercent > 0
    ? `+${absolutePercentLabel}`
    : deltaPercent < 0
      ? `-${absolutePercentLabel}`
      : absolutePercentLabel;

  return {
    deltaMinutes,
    deltaLabel,
    deltaPercent,
    deltaPercentLabel,
  };
}